import { type FC, useMemo } from 'react'
import {
  type GridCellParams,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowModel,
  type GridRowSelectionModel,
  type GridRowsProp,
  type GridSortDirection,
  type GridRowParams,
  type GridSortModel,
} from '@mui/x-data-grid'
import { Stack } from '@mui/material'
import { DataGridPro as MuiDataGridPro, type DataGridProProps as MuiDataGridProProps } from '@mui/x-data-grid-pro'
import { nlNL } from './DataGridLocale.js'
import { DataGridSearchHeader, type DataGridSearchHeaderProps } from './DataGridSearchHeader.js'
import { DataGridHeaderBar, type DataGridHeaderBarProps } from './DataGridHeaderBar.js'
import { DataGridFooterBar, type DataGridFooterBarProps } from './DataGridFooterBar.js'

export type {
  GridColDef,
  GridCellParams,
  GridSortModel,
  GridSortDirection,
  GridRenderCellParams,
  GridRowParams,
  GridRowModel,
  GridRowsProp,
  GridRowSelectionModel,
}

export type DataGridProProps = MuiDataGridProProps &
  DataGridHeaderBarProps &
  DataGridFooterBarProps &
  DataGridSearchHeaderProps & {
    maxHeight?: number | string
  }

export const DataGridPro: FC<DataGridProProps> = ({
  title,
  tableActions,
  tableHeaderBar,
  tableFooterBar,
  onSearch,
  onAddRow,
  onRefresh,
  columns,
  maxHeight,
  pagination = true,
  ...dataGridProps
}: DataGridProProps) => {
  // The cell element isn't display: flex by default. You can add display: 'flex' on the column definition to restore the behavior.
  // https://mui.com/x/migration/migration-data-grid-v6/#css-classes-and-styling
  const columnsWithFlex = useMemo(
    () =>
      columns.map(
        (column) =>
          ({
            display: 'flex',
            ...column,
          }) as GridColDef
      ),
    [columns]
  )

  const grid = (
    <MuiDataGridPro
      localeText={nlNL}
      columns={columnsWithFlex}
      disableRowSelectionOnClick
      data-test="datagrid"
      autoHeight={!maxHeight}
      pagination={pagination}
      density="standard"
      {...dataGridProps}
      onCellClick={(params, event) => {
        if (params.field === 'actions') {
          event.stopPropagation()
        }
      }}
    />
  )

  return (
    <>
      {(title || onSearch) && (
        <DataGridSearchHeader
          title={title}
          tableActions={tableActions}
          onSearch={onSearch}
          onAddRow={onAddRow}
          onRefresh={onRefresh}
        />
      )}
      {tableHeaderBar && <DataGridHeaderBar tableHeaderBar={tableHeaderBar} />}
      {maxHeight ? (
        <Stack maxHeight={maxHeight} sx={{ width: '100%', minHeight: 0 }}>
          {grid}
        </Stack>
      ) : (
        grid
      )}

      {tableFooterBar && <DataGridFooterBar tableFooterBar={tableFooterBar} />}
    </>
  )
}
